import { useState } from "react";
import {
  Banknote,
  CreditCard,
  Image as ImageIcon,
  MoreHorizontal,
  Plus,
  Receipt,
  Smartphone,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/v2/ConfirmDialog";
import { EmptyState } from "@/components/v2/EmptyState";
import { PaymentMethodDialog } from "@/components/v2/PaymentMethodDialog";
import {
  useSoftDeletePaymentMethod,
  useUpdatePaymentMethod,
  usePaymentMethods,
  type PaymentMethod,
  type PaymentMethodType,
} from "@/lib/payment-methods";
import { useToast } from "@/components/v2/Toast";

/**
 * `/workspace/payment-methods` — operator manages the rows that
 * appear on the public booking page (full info + QR) and in the
 * Record Payment dialog (compact, masked).
 */
export function WorkspacePaymentMethods() {
  const list = usePaymentMethods();
  const update = useUpdatePaymentMethod();
  const remove = useSoftDeletePaymentMethod();
  const toast = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethod | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PaymentMethod | null>(null);

  const open = (m: PaymentMethod | null) => {
    setEditing(m);
    setDialogOpen(true);
  };

  const onToggleActive = (m: PaymentMethod) => {
    update.mutate(
      { id: m.id, active: !m.active },
      {
        onSuccess: () =>
          toast.success(m.active ? "Method hidden" : "Method active"),
        onError: (e) => toast.error("Couldn't toggle", (e as Error).message),
      },
    );
  };

  const onConfirmDelete = () => {
    if (!pendingDelete) return;
    remove.mutate(pendingDelete.id, {
      onSuccess: () => {
        toast.success("Method hidden");
        setPendingDelete(null);
      },
      onError: (e) => {
        toast.error("Couldn't hide", (e as Error).message);
        setPendingDelete(null);
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold tracking-tight">Payment methods</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Methods you accept. Customers see the full info on the public
            booking page; operators see a compact label when recording payments.
          </p>
        </div>
        <Button size="sm" onClick={() => open(null)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Method
        </Button>
      </div>

      {list.isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      {list.isError && (
        <p className="text-sm text-destructive">{(list.error as Error).message}</p>
      )}

      {list.data && list.data.length === 0 && (
        <EmptyState
          icon={Receipt}
          title="No payment methods yet"
          description="Add a Bank account, an E-wallet (GCash, PayMaya), Cash / pay-in-person, or any Other method customers can use to pay."
          action={
            <Button size="sm" onClick={() => open(null)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add your first method
            </Button>
          }
        />
      )}

      {list.data && list.data.length > 0 && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b border-border text-left">
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Method
                </th>
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground w-32">
                  Type
                </th>
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Account
                </th>
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground w-20 text-center">
                  QR
                </th>
                <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground w-24 text-center">
                  Active
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {list.data.map((m) => (
                <Row
                  key={m.id}
                  m={m}
                  onEdit={() => open(m)}
                  onToggle={() => onToggleActive(m)}
                  onDelete={() => setPendingDelete(m)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PaymentMethodDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        editing={editing}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={onConfirmDelete}
        title={`Hide "${pendingDelete?.displayName ?? ""}"?`}
        description="This soft-deletes the method (sets it inactive). Existing payments keep their reference. You can reactivate it later by editing the row."
        confirmLabel="Hide method"
        pending={remove.isPending}
      />
    </div>
  );
}

function Row({
  m,
  onEdit,
  onToggle,
  onDelete,
}: {
  m: PaymentMethod;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <tr
      className="border-b border-border last:border-0 group cursor-pointer hover:bg-muted/30"
      onClick={onEdit}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`h-8 w-8 rounded-md inline-flex items-center justify-center shrink-0 ${
              m.methodType === "CASH"
                ? "bg-status-bg-completed text-status-completed"
                : m.methodType === "BANK"
                  ? "bg-primary-light text-primary"
                  : m.methodType === "EWALLET"
                    ? "bg-status-bg-pending text-status-pending"
                    : "bg-muted text-foreground"
            }`}
          >
            {iconFor(m.methodType)}
          </span>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{m.displayName}</p>
            {m.provider && m.methodType !== "CASH" && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {m.provider}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <TypePill kind={m.methodType} />
      </td>
      <td className="px-4 py-3 text-sm">
        {m.methodType === "CASH" ? (
          <span className="text-muted-foreground italic">—</span>
        ) : m.accountNumber ? (
          <div className="space-y-0.5">
            {m.accountName && (
              <p className="text-xs text-muted-foreground truncate">
                {m.accountName}
              </p>
            )}
            <p className="font-mono tabular-nums">****{lastFour(m.accountNumber)}</p>
          </div>
        ) : (
          <span className="text-muted-foreground italic">No account info</span>
        )}
      </td>
      <td className="px-4 py-3 text-center" onClick={stop}>
        {m.qrCodeUrl ? (
          <a
            href={m.qrCodeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block h-10 w-10 rounded border bg-muted/30 overflow-hidden hover:opacity-80"
            title="Open QR"
          >
            <img
              src={m.qrCodeUrl}
              alt="QR"
              className="h-full w-full object-contain"
            />
          </a>
        ) : (
          <ImageIcon className="h-4 w-4 text-muted-foreground/50 inline-block" />
        )}
      </td>
      <td className="px-4 py-3 text-center" onClick={stop}>
        <Toggle checked={m.active} onChange={onToggle} ariaLabel={`Toggle ${m.displayName}`} />
      </td>
      <td className="px-2 text-right" onClick={stop}>
        <div className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-0.5 transition">
          <button
            type="button"
            onClick={onEdit}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60"
            aria-label={`Edit ${m.displayName}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            aria-label={`Hide ${m.displayName}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TypePill({ kind }: { kind: PaymentMethodType }) {
  const label =
    kind === "BANK"
      ? "Bank"
      : kind === "EWALLET"
        ? "E-wallet"
        : kind === "CASH"
          ? "Cash"
          : "Other";
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/70">
      {label}
    </span>
  );
}

function iconFor(type: PaymentMethodType) {
  if (type === "CASH") return <Banknote className="h-4 w-4" />;
  if (type === "EWALLET") return <Smartphone className="h-4 w-4" />;
  if (type === "BANK") return <CreditCard className="h-4 w-4" />;
  return <Receipt className="h-4 w-4" />;
}

function lastFour(s: string): string {
  return s.replace(/\D/g, "").slice(-4) || "????";
}

function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: () => void;
  ariaLabel: string;
}) {
  return (
    <Switch
      checked={checked}
      onCheckedChange={() => onChange()}
      aria-label={ariaLabel}
    />
  );
}
